import {action, makeAutoObservable, observable, runInAction} from "mobx";
import {inject, injectable} from "inversify";
import type {IJSONProvider, IRound, ITournament} from "data/providers/json/json.provider";
import {Bindings} from "data/constants/bindings";
import {RoundStatus, TournamentStatus} from "data/enums";
import {first, last} from "lodash";
import {AxiosError} from "axios";
import {IAxiosApiError} from "data/types/api";
import type {IModalsStore} from "data/stores/modals/modals.store";

export interface IRoundsStore {
	get getIsLoading(): boolean;

	get list(): IRound[];

	get activeRounds(): IRound[];

	get scheduleRounds(): IRound[];

	get roundsForPicks(): IRound[];

	get completedAndActiveRounds(): IRound[];

	get currentRound(): IRound | undefined;

	get currentRoundForPrediction(): IRound | undefined;

	get scoreRound(): IRound | null;

	get currentSelectedRound(): IRound | undefined;

	get tournamentsBySelectedRound(): ITournament[];

	get scheduledTournaments(): ITournament[];

	get completedAndActivePlayoffRounds(): IRound[];

	get completedAndActiveSeasonRounds(): IRound[];

	get isPlayoff(): boolean;

	setSelectedRound(round: IRound | undefined): void;

	fetchRounds(): Promise<void>;
}

@injectable()
export class RoundsStore implements IRoundsStore {
	@observable private _isLoading: boolean = false;
	private _playoffStartRoundId = 22;

	constructor(
		@inject(Bindings.JSONProvider) private _jsonProvider: IJSONProvider,
		@inject(Bindings.ModalsStore) private _modalsStore: IModalsStore
	) {
		makeAutoObservable(this);
	}

	@observable private _list: IRound[] = [];

	@observable private _currentSelectedRound: IRound | undefined;

	get list() {
		return this._list.map((round, index) => ({
			...round,
			roundNumber: ++index,
		}));
	}

	get scheduleRounds() {
		return this.list.filter((e) => e.status === RoundStatus.Scheduled);
	}

	get activeRounds() {
		return this.list.filter((e) =>
			[RoundStatus.Active, RoundStatus.Scheduled].includes(e.status)
		);
	}

	get roundsForPicks() {
		return this.list.filter((round) => round.isPublished);
	}

	get completedRounds() {
		return this.list.filter((e) => e.status === RoundStatus.Complete);
	}

	get completedAndActiveRounds() {
		return this.list.filter(
			(e) => e.status === RoundStatus.Complete || e.status === RoundStatus.Active
		);
	}

	get getIsLoading(): boolean {
		return this._isLoading;
	}

	get currentRound() {
		return this.activeRound || first(this.scheduleRounds) || last(this.list);
	}

	get currentRoundForPrediction() {
		return (
			this.roundsForPicks.find(
				(round) =>
					round.status === RoundStatus.Active || round.status === RoundStatus.Scheduled
			) || last(this.roundsForPicks)
		);
	}

	get scoreRound() {
		return this.activeRound || last(this.completedRounds) || null;
	}

	get tournamentsBySelectedRound() {
		return this._currentSelectedRound?.tournaments || [];
	}

	get scheduledTournaments() {
		return this.tournamentsBySelectedRound.filter(
			({status}) => status === TournamentStatus.Scheduled
		);
	}

	get currentSelectedRound() {
		return this._currentSelectedRound;
	}

	private get activeRound() {
		return this.list.find((e) => e.status === RoundStatus.Active);
	}

	get completedAndActivePlayoffRounds() {
		return this.completedAndActiveRounds.filter(({id}) => id >= this._playoffStartRoundId);
	}

	get completedAndActiveSeasonRounds() {
		return this.completedAndActiveRounds.filter(({id}) => id < this._playoffStartRoundId);
	}

	get isPlayoff() {
		return Boolean(this.completedAndActivePlayoffRounds.length);
	}

	public setSelectedRound(round: IRound | undefined): void {
		this._currentSelectedRound = round;
	}

	@action
	public async fetchRounds(): Promise<void> {
		try {
			this._isLoading = true;
			const {data} = await this._jsonProvider.rounds();
			runInAction(() => {
				this._list = data;
			});
		} catch (e) {
			this._isLoading = false;
			const error = e as AxiosError<IAxiosApiError, unknown>;
			this._modalsStore.showAxiosError(error);
			return Promise.reject(e);
		} finally {
			this.setSelectedRound(this.currentRound);
			this._isLoading = false;
		}
	}
}
